import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { DataBerkas, SysBerkas } from "@/types";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { LuUpload } from "react-icons/lu";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const postData = async (newData: FormData) => {
  const id_sys_periode = newData.get("id_sys_periode");
  const response = await api.post(
    `/siswa/penerimaan/smbjm/data-berkas/upload?id_sys_periode=${id_sys_periode}`,
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export default function Form({
  method,
  closeModal,
  selectedRow,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: SysBerkas | null;
  idDataPendaftaran?: string;
}) {
  const { user } = useUser();
  // const [searchParams] = useSearchParams();
  // const sessionPeriode?.id_sys_periode = searchParams.get("periode");
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [form, setForm] = useState<DataBerkas>({
    id_data_berkas_siswa: "",
    id_data_pendaftaran: idDataPendaftaran || "",
    id_sys_berkas: String(selectedRow?.id_sys_berkas) || "",
    nomor: selectedRow?.data_berkas?.nomor || "",
  });
  const [file, setFile] = useState<File | null>(null);

  const isPost = method === "POST";

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : undefined,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataBerkas"] });
      queryClient.invalidateQueries({
        queryKey: ["dataStep", user?.id, sessionPeriode?.id_sys_periode],
      });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) setFile(file);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData();

    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append("id_sys_berkas", form.id_sys_berkas);
    formData.append("nomor", form.nomor || "");

    if (file) formData.append("file_berkas", file);
    if (sessionPeriode?.id_sys_periode)
      formData.append("id_sys_periode", sessionPeriode?.id_sys_periode);

    mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormHeader icon={<LuUpload />} title={"Upload Data Berkas"} />
      <section>
        <div>
          <Label>File Berkas</Label>
          <Input
            type="file"
            accept=".pdf, image/*"
            onChange={handleFileChange}
          />

          <div>
            {selectedRow &&
              selectedRow.extension_allowed &&
              Array.isArray(selectedRow.extension_allowed) &&
              selectedRow.extension_allowed?.length > 0 && (
                <>
                  <span className="text-sm">
                    Ekstensi file yang diterima :{" "}
                  </span>
                  {selectedRow?.extension_allowed?.map((value: string) => {
                    return <Badge variant={"dark"}>.{value}</Badge>;
                  })}
                </>
              )}
          </div>

          <div>
            {selectedRow && selectedRow.max_upload ? (
              <>
                <span className="text-sm">Ukuran file maksimal : </span>
                <Badge variant={"dark"}>{selectedRow?.max_upload} KB</Badge>
              </>
            ) : (
              ""
            )}
          </div>
        </div>
        {selectedRow?.jenis_berkas?.is_add_filed_tambahan ? (
          <div className="mt-3">
            <Label>Nomor</Label>
            <Input
              required
              placeholder="Ketik nomor.."
              value={form.nomor}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, nomor: event.target.value }))
              }
            />
            <InputError
              text={error?.response?.data?.errors?.nomor?.join(",")}
            />
          </div>
        ) : (
          ""
        )}
      </section>
      <div className="mt-5 flex gap-3">
        <Button variant={"outline"} onClick={closeModal} disabled={isPending}>
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan File Berkas"
          )}
        </Button>
      </div>
    </form>
  );
}
